import * as React from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    NavigationListItemLink,
    NavigationChevronIconWrapper,
    NavigationStarFillIconWrapper,
    NavigationStarOutlineIconWrapper,
    NavigationListWrapper,
    NavigationIconWrapper,
} from './NavigationPanel.style';
import { GoogleTagManagerState } from 'src/domains/layouts/state/googleState/GoogleTagManagerState';
import { Panel } from './Panel';
import { RouteViewType } from 'src/domains/layouts/state/router/newRouter/mainRouteTypes';
import { RightHandSideViewType } from 'src/domains/layouts/state/router/newRouter/rhsRouteType';
import { PromotedEventsNavigationLink } from 'src/domains/layouts/webview/modules/layoutSkeleton/NavigationSidebar/NavigationSidebarPromotedEvents';
import { AmericanFootballIcon } from 'src/ui/assets/icons/sports/AmericanFootballIcon';
import { BasketballIcon } from 'src/ui/assets/icons/sports/BasketballIcon';
import { BaseballIcon } from 'src/ui/assets/icons/sports/BaseballIcon';
import { BoxingIcon } from 'src/ui/assets/icons/sports/BoxingIcon';
import { CricketIcon } from 'src/ui/assets/icons/sports/CricketIcon';
import { DartsIcon } from 'src/ui/assets/icons/sports/DartsIcon';
import { FootballIcon } from 'src/ui/assets/icons/sports/FootballIcon';
import { GolfIcon } from 'src/ui/assets/icons/sports/GolfIcon';
import { GreyhoundRacingIcon } from 'src/ui/assets/icons/sports/GreyhoundRacingIcon';
import { HorseRacingIcon } from 'src/ui/assets/icons/sports/HorseRacingIcon';
import { MMAIcon } from 'src/ui/assets/icons/sports/MMAIcon';
import { OtherSportIcon } from 'src/ui/assets/icons/sports/OtherSportIcon';
import { PoliticsIcon } from 'src/ui/assets/icons/sports/PoliticsIcon';
import { QuestionIcon } from 'src/ui/assets/icons/other/QuestionIcon';
import { RugbyLeagueIcon } from 'src/ui/assets/icons/sports/RugbyLeagueIcon';
import { TennisIcon } from 'src/ui/assets/icons/sports/TennisIcon';
import { NavMenuVariantType } from 'src/domains/layouts/config/features/types';
import { CyclingIcon } from 'src/ui/assets/icons/sports/CyclingIcon';
import { FormulaOneIcon } from 'src/ui/assets/icons/sports/FormulaOneIcon';
import { GaelicHurlingIcon } from 'src/ui/assets/icons/sports/GaelicHurlingIcon';
import { IceHockeyIcon } from 'src/ui/assets/icons/sports/IceHockeyIcon';
import { RugbyUnionIcon } from 'src/ui/assets/icons/sports/RugbyUnionIcon';
import { SnookerIcon } from 'src/ui/assets/icons/sports/SnookerIcon';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { VolleyballIcon } from 'src/ui/assets/icons/sports/VolleyballIcon';
import { GaelicFootballIcon } from 'src/ui/assets/icons/sports/GaelicFootball';
import { MotorSportIcon } from 'src/ui/assets/icons/sports/MotorSportsIcon';
import { WrestlingIcon } from 'src/ui/assets/icons/sports/WrestlingIcon';
import { ChatIcon } from 'src/ui/assets/icons/other/ChatIcon';
import { EighteenPlusIcon } from 'src/ui/assets/icons/other/EighteenPlusIcon';
import { StarFillIcon } from 'src/domains/layouts/shared/assets/icons/StarFillIcon';
import { AustralianRulesIcon } from 'src/ui/assets/icons/sports/AustralianRulesIcon';
import { AppLayoutsState } from 'src/domains/layouts/state/AppLayouts.state';
import { AthleticsIcon } from 'src/ui/assets/icons/sports/AthleticsIcon';
import { BowlsIcon } from 'src/ui/assets/icons/sports/BowlsIcon';
import { ChristmasIcon } from 'src/ui/assets/icons/sports/ChristmasIcon';
import { EurovisionIcon } from 'src/ui/assets/icons/sports/EurovisionIcon';
import { FishingIcon } from 'src/ui/assets/icons/sports/FishingIcon';
import { HandBallIcon } from 'src/ui/assets/icons/sports/HandBallIcon';
import { OlympicsWinterIcon } from 'src/ui/assets/icons/sports/OlympicsWinterIcon';
import { OlympicsSummerIcon } from 'src/ui/assets/icons/sports/OlympicsSummerIcon';
import { PoolIcon } from 'src/ui/assets/icons/sports/PoolIcon';
import { RowingIcon } from 'src/ui/assets/icons/sports/RowingIcon';
import { SwimmingIcon } from 'src/ui/assets/icons/sports/SwimmingIcon';
import { NetBallIcon } from 'src/ui/assets/icons/sports/NetBallIcon';

type IconType = 'ChevronRight' | 'StarFill' | 'StarOutline';

export interface LinkItemLinkType {
    key: string;
    to: RouteViewType | RightHandSideViewType | string;
    label: JSX.Element | string;
    iconType?: IconType;
    iconClick?: () => void;
}

const renderSportIcon = (name: string | null): JSX.Element => {
    const normalized = name?.trim().toLowerCase().replace(/[ .-]/g, '') ?? '';

    const iconMap: Record<string, JSX.Element> = {
        americanfootball: <AmericanFootballIcon />,
        australianrules: <AustralianRulesIcon />,
        athletics: <AthleticsIcon />,
        baseball: <BaseballIcon />,
        basketball: <BasketballIcon />,
        boxing: <BoxingIcon />,
        bowls: <BowlsIcon />,
        cricket: <CricketIcon />,
        cycling: <CyclingIcon />,
        christmas: <ChristmasIcon />,
        darts: <DartsIcon />,
        eurovision: <EurovisionIcon />,
        football: <FootballIcon />,
        formulaone: <FormulaOneIcon />,
        gaelicfootball: <GaelicFootballIcon />,
        gaelichurling: <GaelicHurlingIcon />,
        golf: <GolfIcon />,
        greyhoundracing: <GreyhoundRacingIcon />,
        fishing: <FishingIcon />,
        horseracing: <HorseRacingIcon />,
        handball: <HandBallIcon />,
        icehockey: <IceHockeyIcon />,
        mixedmartialarts: <MMAIcon />,
        motorsport: <MotorSportIcon />,
        netball: <NetBallIcon />,
        olympicswinter: <OlympicsWinterIcon />,
        olympicsummer: <OlympicsSummerIcon />,
        politics: <PoliticsIcon />,
        pool: <PoolIcon />,
        rugbyleague: <RugbyLeagueIcon />,
        rugbyunion: <RugbyUnionIcon />,
        rowing: <RowingIcon />,
        snooker: <SnookerIcon />,
        starboosts: <StarFillIcon />,
        tennis: <TennisIcon />,
        volleyball: <VolleyballIcon />,
        faq: <QuestionIcon />,
        safergambling: <EighteenPlusIcon />,
        swimming: <SwimmingIcon />,
        wrestling: <WrestlingIcon />,
        traderchat: <ChatIcon />,
    };

    if (normalized in iconMap) {
        return (
            <NavigationIconWrapper
                key={normalized}
                isStarBoosts={normalized === 'starboosts'}
            >
                {iconMap[normalized]}
            </NavigationIconWrapper>
        );
    }

    return (
        <NavigationIconWrapper
            key={normalized}
            isStarBoosts={false}
        >
            <OtherSportIcon />
        </NavigationIconWrapper>
    );
};

const renderIcon = (iconParam: IconType | undefined, iconClick?: () => void): React.ReactElement => {
    const icon: IconType = iconParam ?? 'ChevronRight';

    const onClick = (event: React.SyntheticEvent): void => {
        if (iconClick === undefined) {
            return;
        }

        event.stopPropagation();
        event.preventDefault();
        iconClick();
    };

    if (icon === 'ChevronRight') {
        return (
            <NavigationChevronIconWrapper
                position='right'
                onClick={onClick}
            />
        );
    }

    if (icon === 'StarFill') {
        return <NavigationStarFillIconWrapper onClick={onClick} />;
    }

    return <NavigationStarOutlineIconWrapper onClick={onClick} />;
};

const renderItem = (
    link: LinkItemLinkType,
    typeTag: string | undefined,
    googleTagManager: GoogleTagManagerState,
    isCapitalized: boolean,
    toggleMenu: () => void,
    variant: NavMenuVariantType | null
): React.ReactElement => {
    const fireGtmTag = (): void => {
        if (typeTag === 'sport' && typeof link.to !== 'string') {
            if ('name' in link.to && (link.to.name === 'sport' || link.to.name === 'sport-special')) {
                googleTagManager.sportViewTag(link.key, link.to.id);
            }
        }
        toggleMenu();
    };

    if (link.key === 'promotedevents') {
        return <PromotedEventsNavigationLink key={link.key} />;
    }

    return (
        <NavigationListItemLink
            variant={variant}
            key={link.key}
            to={link.to}
            onClick={fireGtmTag}
            isCapitalized={isCapitalized}
        >
            {variant === NavMenuVariantType.SECONDARY &&
                (typeTag === 'sport' || typeTag === 'help-support') &&
                renderSportIcon(link.key)}
            {link.label}
            {renderIcon(link.iconType, link.iconClick)}
        </NavigationListItemLink>
    );
};

interface PropsType {
    title: string | JSX.Element;
    titleDataTest: string;
    links: Array<LinkItemLinkType>;
    isAzMobile?: boolean;
    type?: 'sports' | 'favourites';
    typeTag?: 'sport' | 'help-support';
}

export const NavigationPanel = observer('NavigationPanel', (props: PropsType): React.ReactElement | null => {
    const { title, titleDataTest, links, isAzMobile, typeTag } = props;
    const common = useCommon();
    const googleTagManager = GoogleTagManagerState.get(common);
    const configState = ConfigComponents.get(common).config;
    const appLayoutsState = AppLayoutsState.get(common);
    const { overlay } = appLayoutsState;

    if (links.length === 0) {
        return null;
    }

    const toggleMenuCallback = isAzMobile === true ? (): void => {} : overlay.toggleMenu;

    return (
        <Panel
            title={title}
            titleDataTest={titleDataTest}
            isAzMobile={isAzMobile}
        >
            <NavigationListWrapper>
                {links.map((link) => {
                    return renderItem(
                        link,
                        typeTag,
                        googleTagManager,
                        configState.sportsNamesCapitalized,
                        toggleMenuCallback,
                        configState.componentVariants.navMenuVariant
                    );
                })}
            </NavigationListWrapper>
        </Panel>
    );
});
